import { LeftOutlined } from '@ant-design/icons'
import { Button, Input, Popconfirm, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySubmitErrorBox,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  FOOD_SAFETY_DOCUMENT_CATEGORY,
  FOOD_SAFETY_DOCUMENT_TITLE_MAX_LENGTH,
  FOOD_SAFETY_SECONDARY_TEXT,
} from 'src/constants/foodSafety'
import useIsBlocked from 'src/hooks/useIsBlocked'
import {
  fetchFoodSafetyDocumentDownload,
  fetchFoodSafetyDocuments,
  removeFoodSafetyDocument,
  uploadFoodSafetyDocument,
} from 'src/utils/foodSafetyApi'
import {
  FOOD_SAFETY_DOCUMENT_ACCEPT,
  FOOD_SAFETY_DOCUMENT_CATEGORY_OPTIONS,
  buildFoodSafetyDocumentFilters,
  documentTitleFromFileName,
  formatFoodSafetyDocumentRow,
  formatFoodSafetyDocumentSize,
  validateFoodSafetyDocumentFile,
} from 'src/utils/foodSafetyDocuments'

const emptyUpload = () => ({
  file: null,
  title: '',
  category: FOOD_SAFETY_DOCUMENT_CATEGORY.OTHER,
})

const FoodSafetyDocumentsScreen = () => {
  const blocked = useIsBlocked('food-safety')
  const { venueId: routeVenueId } = useParams()
  const venue = useSelector(state => state.venue?.venue)
  const venueId = String(venue?._id || routeVenueId || '')

  const [documents, setDocuments] = useState([])
  const [canRemove, setCanRemove] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [filter, setFilter] = useState(null)
  const [draft, setDraft] = useState(emptyUpload)
  const [uploadError, setUploadError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [openingId, setOpeningId] = useState(null)
  const fileInputRef = useRef(null)

  const load = useCallback(async () => {
    if (!venueId) return
    setLoadError('')
    try {
      const data = await fetchFoodSafetyDocuments(venueId)
      setDocuments(data?.documents || [])
      setCanRemove(Boolean(data?.canRemove))
    } catch (error) {
      setLoadError(error?.message || 'Could not load documents.')
    } finally {
      setLoading(false)
    }
  }, [venueId])

  useEffect(() => {
    load()
  }, [load])

  const filters = useMemo(
    () => buildFoodSafetyDocumentFilters(documents),
    [documents],
  )
  const shown = useMemo(
    () => (filter ? documents.filter(d => d.category === filter) : documents),
    [documents, filter],
  )

  useEffect(() => {
    if (filter && !documents.some(d => d.category === filter)) setFilter(null)
  }, [documents, filter])

  const chooseFile = event => {
    const file = event.target.files?.[0] || null
    event.target.value = ''
    if (!file) return
    const error = validateFoodSafetyDocumentFile(file)
    setUploadError(error || '')
    if (error) return
    setDraft(prev => ({
      ...prev,
      file,
      title: prev.title || documentTitleFromFileName(file.name),
    }))
  }

  const upload = async () => {
    if (uploading) return
    const error = validateFoodSafetyDocumentFile(draft.file)
    if (error) {
      setUploadError(error)
      return
    }
    setUploading(true)
    setUploadError('')
    try {
      const data = await uploadFoodSafetyDocument(venueId, {
        file: draft.file,
        title: draft.title.trim(),
        category: draft.category,
      })
      const saved = data?.document
      if (saved) setDocuments(prev => [saved, ...prev])
      setDraft(emptyUpload())
      message.success(`${saved?.title || 'Document'} saved`)
    } catch (err) {
      setUploadError(err?.message || 'Could not upload the document.')
    } finally {
      setUploading(false)
    }
  }

  const open = async document => {
    setOpeningId(document._id)
    // Opened before the request so the browser doesn't block it as a pop-up.
    const tab = window.open('', '_blank')
    try {
      const data = await fetchFoodSafetyDocumentDownload(venueId, document._id)
      if (tab) tab.location.href = data.url
      else window.location.assign(data.url)
    } catch (err) {
      if (tab) tab.close()
      message.error(err?.message || 'Could not open the document.')
    } finally {
      setOpeningId(null)
    }
  }

  const remove = async document => {
    try {
      await removeFoodSafetyDocument(venueId, document._id)
      setDocuments(prev => prev.filter(d => d._id !== document._id))
      message.success(`${document.title} removed`)
    } catch (err) {
      message.error(err?.message || 'Could not remove the document.')
    }
  }

  if (blocked) return <PermissionBlockScreen />
  if (!venueId || loading) return <Loading />

  return (
    <Wrapper>
      <TopBar>
        <BackLink to={`/v/${venueId}/food-safety`} data-testid='fs-docs-back'>
          <LeftOutlined /> Today’s overview
        </BackLink>
      </TopBar>

      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>Documents</FoodSafetyPageTitle>
            <BodyText>
              Keep pest control reports, certificates, training records and
              other paperwork in one place, so you can find them when you need
              them.
            </BodyText>
          </div>
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>

      <Card data-testid='fs-docs-upload'>
        <H3>Upload a document</H3>
        <input
          ref={fileInputRef}
          type='file'
          accept={FOOD_SAFETY_DOCUMENT_ACCEPT}
          onChange={chooseFile}
          style={{ display: 'none' }}
          data-testid='fs-docs-file-input'
        />
        <FileRow>
          <Button
            onClick={() => fileInputRef.current?.click()}
            data-testid='fs-docs-choose'
          >
            {draft.file ? 'Choose a different file' : 'Choose file'}
          </Button>
          <Hint data-testid='fs-docs-file-name'>
            {draft.file
              ? `${draft.file.name} · ${formatFoodSafetyDocumentSize(
                  draft.file.size,
                )}`
              : 'PDF, photo, Word or Excel — up to 20 MB'}
          </Hint>
        </FileRow>
        {draft.file ? (
          <>
            <Field>
              <Label as='label' htmlFor='fs-docs-title'>
                Title
              </Label>
              <Input
                id='fs-docs-title'
                value={draft.title}
                maxLength={FOOD_SAFETY_DOCUMENT_TITLE_MAX_LENGTH}
                onChange={e =>
                  setDraft(prev => ({ ...prev, title: e.target.value }))
                }
                onPressEnter={upload}
                placeholder='e.g. Pest control visit — October'
                data-testid='fs-docs-title'
              />
            </Field>
            <Field>
              <Label>Category</Label>
              <ChipRow role='group' aria-label='Category'>
                {FOOD_SAFETY_DOCUMENT_CATEGORY_OPTIONS.map(
                  ({ category, label }) => (
                    <Chip
                      key={category}
                      type='button'
                      data-active={
                        draft.category === category ? 'true' : 'false'
                      }
                      aria-pressed={draft.category === category}
                      onClick={() => setDraft(prev => ({ ...prev, category }))}
                      data-testid={`fs-docs-category-${category}`}
                    >
                      {label}
                    </Chip>
                  ),
                )}
              </ChipRow>
            </Field>
            <Actions>
              <Button
                type='primary'
                loading={uploading}
                onClick={upload}
                data-testid='fs-docs-upload-button'
              >
                Upload
              </Button>
              <Button
                type='link'
                disabled={uploading}
                onClick={() => {
                  setDraft(emptyUpload())
                  setUploadError('')
                }}
              >
                Cancel
              </Button>
            </Actions>
          </>
        ) : null}
        {uploadError ? (
          <FoodSafetySubmitErrorBox role='alert' data-testid='fs-docs-error'>
            {uploadError}
          </FoodSafetySubmitErrorBox>
        ) : null}
      </Card>

      {loadError ? (
        <Card data-testid='fs-docs-load-error'>
          <BodyText>{loadError}</BodyText>
          <Button onClick={load}>Try again</Button>
        </Card>
      ) : documents.length === 0 ? (
        <EmptyCard data-testid='fs-docs-empty'>
          <H3>No documents yet</H3>
          <BodyText>
            Upload your first one above — for example your latest pest control
            report.
          </BodyText>
        </EmptyCard>
      ) : (
        <Card>
          {filters.length > 2 ? (
            <ChipRow role='group' aria-label='Show'>
              {filters.map(f => (
                <Chip
                  key={f.category || 'all'}
                  type='button'
                  data-active={filter === f.category ? 'true' : 'false'}
                  aria-pressed={filter === f.category}
                  onClick={() => setFilter(f.category)}
                  data-testid={`fs-docs-filter-${f.category || 'all'}`}
                >
                  {f.label} · {f.count}
                </Chip>
              ))}
            </ChipRow>
          ) : null}
          <DocList data-testid='fs-docs-list'>
            {shown.map(document => {
              const row = formatFoodSafetyDocumentRow(document)
              return (
                <DocRow
                  key={document._id}
                  data-testid={`fs-docs-row-${document._id}`}
                >
                  <DocText>
                    <DocTitle>{document.title}</DocTitle>
                    <DocDetail>{row.detail}</DocDetail>
                    <DocDetail>{row.meta}</DocDetail>
                  </DocText>
                  <DocActions>
                    <Button
                      onClick={() => open(document)}
                      loading={openingId === document._id}
                      data-testid={`fs-docs-open-${document._id}`}
                    >
                      Open
                    </Button>
                    {canRemove ? (
                      <Popconfirm
                        title={`Remove “${document.title}”? It won’t show here any more.`}
                        okText='Remove'
                        cancelText='Keep'
                        onConfirm={() => remove(document)}
                      >
                        <Button
                          type='link'
                          danger
                          data-testid={`fs-docs-remove-${document._id}`}
                        >
                          Remove
                        </Button>
                      </Popconfirm>
                    ) : null}
                  </DocActions>
                </DocRow>
              )
            })}
          </DocList>
        </Card>
      )}
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 42rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
  `};
`

const TopBar = styled.div``

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  font-weight: 600;
`

const Card = styled(FoodSafetySurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const EmptyCard = styled(Card)`
  border-style: dashed;
`

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`

const Hint = styled.span`
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  word-break: break-word;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const Label = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Chip = styled.button`
  appearance: none;
  border: 1px solid
    ${({ theme, ...p }) =>
      p['data-active'] === 'true'
        ? theme.colours.lettuceGreen || '#7cb305'
        : theme.colours.mayoBorder || '#d9d9d9'};
  background: ${({ theme, ...p }) =>
    p['data-active'] === 'true'
      ? theme.colours.lettuceGreenLight || '#f6ffed'
      : '#fff'};
  color: ${({ theme }) => theme.colours.charcoal || '#262626'};
  border-radius: 999px;
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
  cursor: pointer;
  line-height: 1.3;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DocList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
`

const DocRow = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-top: 1px solid ${({ theme }) => theme.colours.mayoBorder || '#f0f0f0'};

  &:first-child {
    border-top: 0;
  }
`

const DocText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`

const DocTitle = styled.span`
  font-weight: 700;
  word-break: break-word;
`

const DocDetail = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const DocActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
`

export default FoodSafetyDocumentsScreen
